import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import * as Yup from "yup";
import { Form, Input, Typography } from "antd";
import { FaRegSave, FaRegTrashAlt } from "react-icons/fa";
import { RiArrowGoBackLine } from "react-icons/ri";
import { AiOutlineStop } from "react-icons/ai";
import { IoClose } from "react-icons/io5";

import { StyledReasonModal } from "./styles";

const { Text } = Typography;

const MODAL_WIDTH_PX = 560;
const DEFAULT_LOCALE = "pt-BR";
const REASON_MAX_LENGTH = 50;

const toDate = (value) => {
  if (!value) return null;

  const normalized =
    typeof value === "string"
      ? value.replace(/(\.\d{3})\d+([+-]\d{2}:\d{2}|Z)$/, "$1$2")
      : value;

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value, { locale = DEFAULT_LOCALE } = {}) => {
  const date = toDate(value);
  if (!date) return null;

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const MODES = Object.freeze({
  CREATE: "create",
  VIEW: "view",
  EDIT: "edit",
  DELETE: "delete",
  INACTIVATE: "inactivate",
});

const getReasonSchema = (intl) =>
  Yup.object().shape({
    reason: Yup.string()
      .trim()
      .required(intl.formatMessage({ id: "required_field" })),
  });

const getModalTitleByMode = ({ intl, mode }) => {
  const map = {
    [MODES.CREATE]: "reasonsDisapprovalDocuments.modal.title.create",
    [MODES.VIEW]: "reasonsDisapprovalDocuments.modal.title.view",
    [MODES.EDIT]: "reasonsDisapprovalDocuments.modal.title.edit",
    [MODES.DELETE]: "reasonsDisapprovalDocuments.modal.title.delete",
    [MODES.INACTIVATE]: "reasonsDisapprovalDocuments.modal.inactivate.title",
  };

  return intl.formatMessage({ id: map[mode] });
};

const getOkTextByMode = ({ intl, mode }) => {
  const map = {
    [MODES.CREATE]: "common.actions.save",
    [MODES.VIEW]: "common.actions.close",
    [MODES.EDIT]: "common.actions.save",
    [MODES.DELETE]: "common.actions.delete",
    [MODES.INACTIVATE]: "reasonsDisapprovalDocuments.modal.inactivate.okText",
  };

  return intl.formatMessage({ id: map[mode] });
};

const getCancelTextByMode = ({ intl }) =>
  intl.formatMessage({ id: "common.actions.back" });

const ReasonModal = ({
  open,
  mode,
  initialValues,
  loading,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const isViewMode = mode === MODES.VIEW;
  const isDeleteMode = mode === MODES.DELETE;
  const isInactivateMode = mode === MODES.INACTIVATE;
  const isCreateOrEditMode = mode === MODES.CREATE || mode === MODES.EDIT;

  const title = useMemo(
    () => getModalTitleByMode({ intl, mode }),
    [intl, mode]
  );

  const okText = useMemo(() => getOkTextByMode({ intl, mode }), [intl, mode]);

  const cancelText = useMemo(() => getCancelTextByMode({ intl }), [intl]);

  const normalizedInitialValues = useMemo(
    () => ({
      reason: initialValues?.reason ?? "",
      createdAt: initialValues?.createdAt ?? null,
      updatedAt: initialValues?.updatedAt ?? null,
    }),
    [initialValues]
  );

  const okButtonProps = useMemo(() => {
    if (isDeleteMode) {
      return {
        danger: true,
        disabled: loading,
        icon: <FaRegTrashAlt size={16} />,
      };
    }

    if (isInactivateMode) {
      return {
        danger: true,
        disabled: loading,
        icon: <AiOutlineStop size={16} />,
      };
    }

    if (isViewMode) {
      return {
        disabled: loading,
        icon: <IoClose />,
      };
    }

    return {
      disabled: loading,
      icon: <FaRegSave size={16} />,
    };
  }, [isDeleteMode, isInactivateMode, isViewMode, loading]);

  const cancelButtonProps = useMemo(
    () => ({
      disabled: loading,
      icon: <RiArrowGoBackLine size={16} />,
    }),
    [loading]
  );

  const schema = useMemo(() => getReasonSchema(intl), [intl]);

  const setYupErrorsOnForm = useCallback(
    (validationError) => {
      const nextErrors = validationError?.inner?.length
        ? validationError.inner.map((err) => ({
            name: err.path,
            errors: [err.message],
          }))
        : [];

      form.setFields(nextErrors);
    },
    [form]
  );

  const handleSubmit = useCallback(async () => {
    if (isViewMode) {
      onClose?.();
      return;
    }

    if (isInactivateMode) {
      const id = initialValues?.id ?? initialValues?.key;
      await onDelete?.({ id, values: initialValues }, { confirm: true });
      return;
    }

    if (isDeleteMode) {
      const id = initialValues?.id ?? initialValues?.key;
      await onDelete?.({ id, values: initialValues });
      return;
    }

    try {
      const values = await form.validateFields();
      const payload = { reason: values.reason?.trim() };

      await schema.validate(payload, { abortEarly: false });

      if (mode === MODES.CREATE) {
        await onCreate?.(payload);
        return;
      }

      if (mode === MODES.EDIT) {
        const id = initialValues?.id ?? initialValues?.key;
        await onUpdate?.({ id, values: payload });
      }
    } catch (error) {
      if (error?.name === "ValidationError") {
        setYupErrorsOnForm(error);
        return;
      }

      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [
    form,
    initialValues,
    isDeleteMode,
    isInactivateMode,
    isViewMode,
    mode,
    onClose,
    onCreate,
    onDelete,
    onUpdate,
    schema,
    setYupErrorsOnForm,
  ]);

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue(normalizedInitialValues);
    form.setFields([{ name: "reason", errors: [] }]);
  }, [form, normalizedInitialValues, open]);

  const shouldShowForm = isCreateOrEditMode;
  const inputsDisabled = isViewMode || loading;

  const formattedCreatedAt =
    formatDateTime(normalizedInitialValues.createdAt, {
      locale: intl.locale,
    }) ?? intl.formatMessage({ id: "common.notAvailable" });

  const formattedUpdatedAt =
    formatDateTime(normalizedInitialValues.updatedAt, {
      locale: intl.locale,
    }) ?? intl.formatMessage({ id: "common.notAvailable" });

  return (
    <StyledReasonModal
      open={open}
      title={title}
      okText={okText}
      cancelText={cancelText}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      okButtonProps={okButtonProps}
      cancelButtonProps={cancelButtonProps}
      width={MODAL_WIDTH_PX}
      destroyOnClose
      maskClosable={!loading}
      keyboard={!loading}
    >
      {isDeleteMode ? (
        <>
          <Text>
            {intl.formatMessage({
              id: "reasonsDisapprovalDocuments.modal.delete.description",
            })}
          </Text>

          <Text strong style={{ display: "block", marginTop: 8 }}>
            “{normalizedInitialValues.reason}”
          </Text>
        </>
      ) : isInactivateMode ? (
        <>
          <Text style={{ display: "block" }}>
            {intl.formatMessage({
              id: "reasonsDisapprovalDocuments.modal.inactivate.description",
            })}
          </Text>

          <Text style={{ display: "block", marginTop: 8 }}>
            {intl.formatMessage({
              id: "reasonsDisapprovalDocuments.modal.inactivate.confirm",
            })}
          </Text>
        </>
      ) : isViewMode ? (
        <>
          <Text type="secondary">
            {intl.formatMessage({
              id: "reasonsDisapprovalDocuments.modal.fields.reason.label",
            })}
          </Text>
          <Text style={{ display: "block", marginBottom: 12 }}>
            {normalizedInitialValues.reason || "—"}
          </Text>

          <Text type="secondary">
            {intl.formatMessage({
              id: "reasonsDisapprovalDocuments.modal.fields.createdAt.label",
            })}
          </Text>
          <Text style={{ display: "block", marginBottom: 12 }}>
            {formattedCreatedAt}
          </Text>

          <Text type="secondary">
            {intl.formatMessage({
              id: "reasonsDisapprovalDocuments.modal.fields.updatedAt.label",
            })}
          </Text>
          <Text style={{ display: "block" }}>{formattedUpdatedAt}</Text>
        </>
      ) : (
        shouldShowForm && (
          <Form layout="vertical" form={form} requiredMark={false}>
            <Form.Item
              label={intl.formatMessage({
                id: "reasonsDisapprovalDocuments.modal.fields.reason.label",
              })}
              name="reason"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "reasonsDisapprovalDocuments.modal.validation.reason.required",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "reasonsDisapprovalDocuments.modal.fields.reason.placeholder",
                })}
                style={{ borderRadius: "5px" }}
                disabled={inputsDisabled}
                maxLength={REASON_MAX_LENGTH}
                showCount
              />
            </Form.Item>
          </Form>
        )
      )}
    </StyledReasonModal>
  );
};

ReasonModal.MODES = MODES;

ReasonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(Object.values(MODES)).isRequired,
  initialValues: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reason: PropTypes.string,
    createdAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    updatedAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }),
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
};

ReasonModal.defaultProps = {
  initialValues: null,
  loading: false,
  onCreate: null,
  onUpdate: null,
  onDelete: null,
};

export default ReasonModal;
